'use client'

import { useRef } from 'react'
import { motion, useInView, useReducedMotion } from 'framer-motion'

import { Badge, Button } from '@mdxui/primitives'

import { cn } from '../../lib/cn'
import {
  sectionVariants,
  headerVariants,
  gridContainerVariants,
  gridItemVariants,
  reducedMotionVariants,
} from '../../lib/motion'

interface AddOn {
  name: string
  description: string
  price: {
    unit: string
    amount: string
  }
  buttonText?: string
  buttonUrl?: string
}

const defaultAddOns: AddOn[] = [
  {
    name: 'Automatically refill your credits',
    description:
      "Set your threshold, and we'll auto-refill your credits - no expiration, no interruptions!",
    price: {
      unit: 'Total per 1000 message credits',
      amount: '$14',
    },
  },
  {
    name: 'Bonus message credits',
    description: '$12 per 1000 credits / month',
    price: {
      unit: 'Total per month',
      amount: '$12',
    },
  },
  {
    name: 'Additional agents',
    description: '$7 per AI agent / month',
    price: {
      unit: 'Total per month',
      amount: '$7',
    },
  },
  {
    name: 'Personalized domains',
    description:
      "Use your custom domains for the AI agent's embed script, iframe, and shareable link - fully branded and seamless!",
    price: {
      unit: 'Total per month',
      amount: '$59',
    },
  },
  {
    name: 'Remove Watermark',
    description: 'Remove the branding from the iframe and widget',
    price: {
      unit: 'Total per month',
      amount: '$39',
    },
  },
]

export interface PricingAddOnsProps {
  badge?: string
  heading?: string
  addOns?: AddOn[]
  className?: string
}

const PricingAddOns = ({
  badge = 'Add-ons',
  heading = 'Enhance your plan with powerful add-ons!',
  addOns = defaultAddOns,
  className,
}: PricingAddOnsProps) => {
  const sectionRef = useRef<HTMLElement>(null)
  const isInView = useInView(sectionRef, { once: true, margin: '-100px' })
  const prefersReducedMotion = useReducedMotion()

  const headerAnimation = prefersReducedMotion ? reducedMotionVariants : headerVariants
  const itemAnimation = prefersReducedMotion ? reducedMotionVariants : gridItemVariants

  return (
    <motion.section
      ref={sectionRef}
      className={cn('py-16 lg:py-32', className)}
      variants={sectionVariants}
      initial="hidden"
      animate={isInView ? 'visible' : 'hidden'}
    >
      <div className="container mx-auto max-w-5xl">
        <div className="flex w-full flex-col items-center gap-4">
          <motion.div variants={headerAnimation}>
            <Badge
              variant="outline"
              className="rounded-full border-muted bg-background px-4 py-1.5"
            >
              <span className="text-sm font-medium leading-5 text-foreground">{badge}</span>
            </Badge>
          </motion.div>
          <motion.h2
            className="max-w-full text-center text-3xl font-semibold md:max-w-[42.5rem] lg:text-4xl"
            variants={headerAnimation}
          >
            {heading}
          </motion.h2>
          <motion.div
            className="flex w-full flex-col items-center gap-4 pt-10"
            variants={gridContainerVariants(0.1, 0.2)}
          >
            {addOns.map((addon) => (
              <motion.div
                className="grid w-full gap-6 rounded-[var(--radius-2xl,calc(var(--radius)*2))] border border-border p-6 shadow-sm lg:grid-cols-2"
                key={addon.name}
                variants={itemAnimation}
              >
                {/* Left: Title + Description */}
                <div className="min-w-0">
                  <h3 className="text-xl font-semibold">{addon.name}</h3>
                  <p className="mt-1 text-sm text-muted-foreground">{addon.description}</p>
                </div>

                {/* Right: Price + Button */}
                <div className="flex flex-col items-start gap-3 lg:items-end">
                  <div className="lg:text-right">
                    <div className="text-base font-semibold lg:text-lg">{addon.price.amount}</div>
                    <div className="text-xs text-muted-foreground">{addon.price.unit}</div>
                  </div>
                  <Button variant="outline" className="w-full lg:w-auto" asChild>
                    <a href={addon.buttonUrl || '#'}>{addon.buttonText || 'Get Add-on'}</a>
                  </Button>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>
    </motion.section>
  )
}

export { PricingAddOns }
export type { AddOn as PricingAddOn }
